// ReplayTimeline.tsx (S26) - scrub a played or imported hand street by street. Reference lane #3
// (Lichess move-list / Chess.com Game Review): a compact board strip + hero cards on top, a street
// scrubber (only the streets that happened are lit), then a move-list grouped by street with the
// current street ringed. Honest for imports: imported hands carry no read/archetype data, so the
// timeline shows the LINE only - no grade dots - which is what `segmentHand` produces from the record.
import { useState } from 'react';
import type { CardIndex } from '@postflop/schema';
import { Card } from '../components/Card';
import { decodeCard } from '../engine/cards';
import type { ReplaySegments } from './segment';

const POS_FULL: Record<string, string> = {
  UTG: 'Under the gun',
  UTG1: 'UTG plus one',
  UTG2: 'UTG plus two',
  LJ: 'Lojack',
  HJ: 'Hijack',
  CO: 'Cutoff',
  BTN: 'Button',
  SB: 'Small blind',
  BB: 'Big blind',
};

/** A tight inline board string like "A♥ 7♣ 2♦" for the street header. */
function boardText(board: readonly CardIndex[]): string {
  return board.map((c) => {
    const { rank, glyph } = decodeCard(c);
    return `${rank}${glyph}`;
  }).join(' ');
}

export interface ReplayTimelineProps {
  segments: ReplaySegments;
  /** the source badge copy - "Imported" for a pasted hand, "Played" for one from the trainer. */
  sourceLabel?: string;
}

export function ReplayTimeline({ segments, sourceLabel = 'Imported' }: ReplayTimelineProps) {
  const played = segments.streets.length;
  const [idx, setIdx] = useState(0);
  const cur = segments.streets[Math.min(idx, played - 1)];

  return (
    <div className="replay">
      {/* board strip + hero cards */}
      <div className="rp-board">
        <div className="rp-rail">
          <span className="rp-street">{cur.label}</span>
          <span className="rp-pot num">
            Pot <b>${cur.potAtStart}</b>
          </span>
        </div>
        <div className="rp-commrow">
          <div className="rp-comm">
            {cur.board.map((card, i) => (
              <Card key={i} card={card} />
            ))}
            {Array.from({ length: 5 - cur.board.length }).map((_, i) => (
              <span key={`g${i}`} className="rp-ghost" />
            ))}
          </div>
          <div className="rp-herochip">
            <div className="rp-lb">{sourceLabel === 'Imported' ? 'Hero' : 'You'}</div>
            <div className="rp-cards">
              <Card card={segments.heroCards[0]} />
              <Card card={segments.heroCards[1]} />
            </div>
          </div>
        </div>
        <div className="rp-herolab">
          <span className="rp-you">{POS_FULL[segments.heroPosition] ?? segments.heroPosition}</span>
          {' · '}
          {segments.stakesLabel} {segments.tableSize}-max
        </div>
      </div>

      {/* street scrubber */}
      <div className="streetbar rp-scrub">
        {(['Preflop', 'Flop', 'Turn', 'River'] as const).map((label, i) => {
          const on = i === idx;
          const done = i < idx && i < played;
          const off = i >= played;
          const cls = 'sp' + (done ? ' done' : '') + (on ? ' on' : '') + (off ? ' off' : '');
          return (
            <button
              key={label}
              type="button"
              className={cls}
              disabled={off}
              onClick={() => !off && setIdx(i)}
            >
              <div className="rail" />
              <div className="lb">{label}</div>
            </button>
          );
        })}
      </div>

      {/* move list */}
      <div className="movelist">
        {segments.streets.map((s, si) => {
          const stateCls = si === idx ? ' cur' : si < idx ? ' past' : '';
          const head = s.board.length ? `${s.label} · ${boardText(s.board)}` : s.label;
          return (
            <div key={s.street} className={'mvstreet' + stateCls}>
              <button type="button" className="mvhdr" onClick={() => setIdx(si)}>
                <span className="dot" />
                {head}
              </button>
              {s.moves.map((m, mi) => {
                const rowCls =
                  'mvrow' +
                  (m.kind === 'fold' ? ' fold' : '') +
                  (m.aggressor ? ' agg' : '');
                return (
                  <div key={mi} className={rowCls}>
                    <div className={'who' + (m.isHero ? ' who-hero' : '')}>{m.who}</div>
                    <div className="act">
                      <span className="verb">{m.verb}</span>
                    </div>
                    {m.amount !== undefined && <div className="amt num">${m.amount}</div>}
                  </div>
                );
              })}
            </div>
          );
        })}
        <div className="result">
          {segments.result.heroWon ? (
            <>
              <b>Hero wins ${segments.result.potWon}</b>
              {segments.result.showdown ? ' at showdown' : ' · no showdown'}
            </>
          ) : (
            <>
              Pot ${segments.result.potWon} to the other seat
              {segments.result.showdown ? ' at showdown' : ''}
            </>
          )}
        </div>
      </div>

      {/* dock */}
      <div className="rp-dock">
        <button type="button" onClick={() => setIdx((i) => Math.max(0, i - 1))} disabled={idx === 0}>
          {'←'} Prev
        </button>
        <button
          type="button"
          className="primary"
          onClick={() => setIdx((i) => Math.min(played - 1, i + 1))}
          disabled={idx >= played - 1}
        >
          Next street {'→'}
        </button>
      </div>
      <p className="rp-note">
        Imported hands carry no read data, so the timeline shows the line only. Played hands add the
        move-class grade per decision.
      </p>
    </div>
  );
}
